/**
 * 시장 인사이트 API — /v1/kis/market/*
 */
import { apiClient } from "./client";

export interface MarketSearchResult {
  name: string;
  code: string;
  excd?: string;
}

export interface MarketStockInfo {
  symbol: string;
  name: string;
  per: number;
  pbr: number;
  eps: number;
  bps: number;
  roe: number;
  dividend_yield: number;
  market_cap: number;
  w52_high: number;
  w52_low: number;
  face_value: number;
  listed_shares: number;
  capital: number;
  currency: string;
}

export interface MarketNewsItem {
  id: string;
  title: string;
  source: string;
  datetime: string;
  content_url?: string | null;
  related_stocks?: string[];
  related_stock_names?: string[];
}

export interface MarketCandle {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

/** GET /v1/kis/market/price/{symbol} 응답 (BE kis/api/dto/price.py StockPrice) */
export interface MarketPrice {
  symbol: string;
  current_price: number;
  change_amount: number;
  change_rate: number;
  base_price: number;
}

export const marketApi = {
  search: (query: string, isOverseas = false) =>
    apiClient.get<MarketSearchResult[]>(`/v1/kis/market/search`, {
      q: query,
      is_overseas: isOverseas || undefined,
    }),

  getPrice: (symbol: string, isOverseas = false) =>
    apiClient.get<MarketPrice>(`/v1/kis/market/price/${symbol}`, {
      is_overseas: isOverseas || undefined,
    }),

  getInfo: (symbol: string, isOverseas = false) =>
    apiClient.get<MarketStockInfo>(`/v1/kis/market/info/${symbol}`, {
      is_overseas: isOverseas || undefined,
    }),

  getChart: (symbol: string, days = 120, isOverseas = false) =>
    apiClient.get<{ symbol: string; candles: MarketCandle[] }>(`/v1/kis/market/chart/${symbol}`, {
      days,
      is_overseas: isOverseas || undefined,
    }),

  getNews: (symbol: string, isOverseas = false) =>
    apiClient.get<MarketNewsItem[]>(`/v1/kis/market/news/${symbol}`, {
      is_overseas: isOverseas || undefined,
    }),

  getStockInsight: (symbol: string, isOverseas = false, forceRefresh = false) =>
    apiClient.get<StockInsightVO>(`/v1/insights/stock-insight`, {
      symbol,
      is_overseas: isOverseas || undefined,
      force_refresh: forceRefresh || undefined,
    }, { timeout: 180_000 }), // LLM 생성 최대 90초+ — 기본 30초로는 부족
};

export interface StockInsightVO {
  insight: string;
  generated_at: string;
  from_cache: boolean;
}

/**
 * Market Desk — FinHub AI 펀드 매니저 Phase 1 (2026-08-17)
 */
export interface MarketIndexVO {
  code: string;
  name: string;
  region: string;
  value: number;
  change_amount: number;
  change_rate: number;
  as_of?: string;
}

export interface BreadthRowVO {
  date: string;
  total: number;
  up: number;
  down: number;
  flat: number;
  up_ratio: number;
}

export interface SectorStatVO {
  sector: string;
  count: number;
  market_cap: number;
  mcap_ratio: number;
  avg_per: number | null;
  avg_pbr: number | null;
  avg_div_yield: number | null;
  up_count: number;
  down_count: number;
  up_ratio: number | null;
}

export interface RegimeVO {
  regime: string;
  detail: {
    trend_index: number;
    ma60: number | null;
    ma200: number | null;
    above_ma200: boolean;
    above_ma60: boolean;
    rising_5d: boolean;
  };
  history: { date: string; level: number }[];
}

export const marketDeskApi = {
  getOverview: () =>
    apiClient.get<MarketIndexVO[]>(`/v1/kis/market/desk/overview`),

  getBreadth: (days = 60) =>
    apiClient.get<{ series: BreadthRowVO[]; latest: BreadthRowVO | null }>(`/v1/kis/market/desk/breadth`, { days }),

  getSectors: () =>
    apiClient.get<{ data_date: string; prev_date: string; sectors: SectorStatVO[] }>(`/v1/kis/market/desk/sectors`),

  getRegime: () =>
    apiClient.get<RegimeVO>(`/v1/kis/market/desk/regime`),

  getMarketDeskReport: (forceRefresh = false) =>
    apiClient.get<MarketDeskReportVO>(`/v1/insights/market-desk`, { force_refresh: forceRefresh || undefined }, { timeout: 180_000 }), // LLM 생성 최대 90초+ — 기본 30초로는 부족
};

export interface MarketDeskReportVO {
  report: string;
  generated_at: string;
  from_cache: boolean;
}

/**
 * Screener — FinHub AI 펀드 매니저 Phase 2 (2026-08-17)
 */
export interface ScreenConditionVO {
  factor: string;
  op: "gte" | "lte" | "between";
  value?: number;
  value_min?: number;
  value_max?: number;
}

export interface ScreenedStockVO {
  ticker: string;
  name: string;
  market: string | null;
  sector: string | null;
  price: number | null;
  per: number | null;
  pbr: number | null;
  roe: number | null;
  roic: number | null;
  debt_ratio: number | null;
  div_yield: number | null;
  return_1m: number | null;
  return_3m: number | null;
  return_6m: number | null;
  market_cap: number | null;
  op_profit_growth: number | null;
}

export interface ScreenResultVO {
  data_date: string | null;
  total: number;
  stocks: ScreenedStockVO[];
  universe_stats?: Record<string, number>;
  preset?: { key: string; name: string; description: string };
}

export interface PresetVO {
  key: string;
  name: string;
  description: string;
  conditions: ScreenConditionVO[];
  sort: { factor: string; order: string };
}

export interface SavedScreenVO {
  id: number;
  name: string;
  conditions: ScreenConditionVO[];
  sort_by: string | null;
  sort_order: string;
  created_at: string | null;
}

export const screenerApi = {
  getFactors: () =>
    apiClient.get<{ factor: string; label: string }[]>(`/v1/kis/screener/factors`),

  getPresets: () =>
    apiClient.get<PresetVO[]>(`/v1/kis/screener/presets`),

  runPreset: (key: string, limit = 50) =>
    apiClient.post<ScreenResultVO>(`/v1/kis/screener/presets/${key}/run?limit=${limit}`),

  run: (body: {
    conditions: ScreenConditionVO[];
    sort_by?: string;
    sort_order?: string;
    limit?: number;
    market?: string;
  }) => apiClient.post<ScreenResultVO>(`/v1/kis/screener/run`, body),

  listSaved: () =>
    apiClient.get<SavedScreenVO[]>(`/v1/kis/screener/saved`),

  saveScreen: (body: { name: string; conditions: ScreenConditionVO[]; sort_by?: string; sort_order?: string }) =>
    apiClient.post<{ ok: boolean }>(`/v1/kis/screener/saved`, body),

  deleteSaved: (id: number) =>
    apiClient.delete<{ ok: boolean }>(`/v1/kis/screener/saved/${id}`),

  runCustomBacktest: (body: {
    conditions: ScreenConditionVO[];
    period_months: number;
    top_n: number;
    rebalance_freq: string;
    benchmark?: string;
  }) =>
    apiClient.post<CustomBacktestVO>(`/v1/kis/quant/backtest/custom`, body, { timeout: 300_000 }),
};

export interface CustomBacktestVO {
  strategy: string;
  period: { start: string; end: string; trading_days: number };
  portfolio: {
    initial_value: number;
    final_value: number;
    total_return_pct: number;
    cagr: number;
    sharpe_ratio: number;
    mdd_pct: number;
    win_rate: number;
    volatility_pct: number;
  };
  benchmark: { total_return_pct?: number; cagr?: number } | null;
  rebalances: unknown[];
  daily_values: { date: string; value: number; benchmark?: number }[];
  monthly_returns: unknown[];
}

/**
 * Portfolio Lab — FinHub AI 펀드 매니저 Phase 3 (2026-08-17)
 */
export interface RiskProfileDefVO {
  key: string;
  label: string;
  desc: string;
  target_vol: number;
  equity_max: number;
  base_weights: Record<string, number>;
}

export interface SurveyOptionVO { label: string; score: number }
export interface SurveyQuestionVO {
  id: string;
  q: string;
  options: SurveyOptionVO[];
}

export interface OptimizeWeightsVO {
  key: string;
  label: string;
  weight: number;
}
export interface OptimizeStrategyVO {
  weights: OptimizeWeightsVO[];
  expected_return?: number;
  volatility: number;
  sharpe: number;
}
export interface OptimizeResultVO {
  profile: { key: string; label: string; desc: string; target_vol: number; equity_max: number };
  base_weights: Record<string, number>;
  risk_parity: OptimizeStrategyVO;
  mvo: OptimizeStrategyVO;
  mvo_capped: OptimizeStrategyVO;
  data: { days: number; start: string; end: string };
}

export interface DiagnosisRowVO {
  key: string;
  label: string;
  current_pct: number;
  target_pct: number;
  diff_pct: number;
  current_value: number;
  target_value: number;
  diff_value: number;
}
export interface DiagnosisVO {
  strategy: string;
  target_meta: { label: string; volatility?: number; sharpe?: number };
  total_value: number;
  rows: DiagnosisRowVO[];
  /** 카테고리별 보유 종목 내역 (금액 내림차순) — 행 클릭 확장용 */
  holdings_breakdown: Record<string, { ticker: string; name: string; value: number }[]>;
  suggestions: {
    buys: { key: string; label: string; amount: number; buy_tickers?: string[] }[];
    sells: { key: string; label: string; amount: number; buy_tickers?: string[] }[];
    buys_sum: number;
    sells_sum: number;
  };
  unclassified: { count: number; value: number; items: { ticker: string; name: string; value: number }[] };
  band_threshold_pct: number;
}

/** 전략 백테스트 비교 (Phase 5) */
export interface BacktestCompareVO {
  profile: string;
  rebalance: string;
  start: string;
  end: string;
  trading_days: number;
  strategies: Record<string, {
    cumulative: number;
    annual_return: number;
    mdd: number;
    volatility: number;
  }>;
  curves: Record<string, { dates: string[]; values: number[] }>;
}

/** 매수 전용 리밸런스 플랜 (2026-09-16) */
export interface RebalancePlanRowVO {
  category_key: string;
  category_label: string;
  ticker: string;
  name: string;
  alloc_amount: number;
  price: number;
  price_date: string;
  qty: number;
  currency: "KRW" | "USD";
  gap_pct: number;
}
export interface RebalancePlanVO {
  mode: "buy_only";
  cash_amount: number;
  total_value: number;
  band_threshold_pct: number;
  strategy: string;
  target_meta: { label: string };
  categories_used: string[];
  gold_excluded: string[];
  rows: RebalancePlanRowVO[];
  alloc_total: number;
  leftover: number;
  note: string;
}

export const optimizerApi = {
  getRiskProfiles: () =>
    apiClient.get<{ profiles: RiskProfileDefVO[]; survey: SurveyQuestionVO[] }>(`/v1/optimizer/risk-profiles`),

  getMyProfile: () =>
    apiClient.get<{ saved: boolean; profile_key?: string; strategy_choice?: string | null }>(`/v1/optimizer/risk-profile`),

  saveProfile: (body: {
    answers: { question_id: string; score: number }[];
    strategy_choice?: string;
  }) => apiClient.put<{ ok: boolean; profile_key: string; total_score: number }>(`/v1/optimizer/risk-profile`, body),

  optimize: (profileKey: string) =>
    apiClient.post<OptimizeResultVO>(`/v1/optimizer/optimize?profile_key=${profileKey}`, {}),

  diagnosis: (strategy: string, profile?: string) =>
    apiClient.get<DiagnosisVO>(`/v1/optimizer/diagnosis`, { strategy, profile: profile || undefined }),

  /** 매수 전용 리밸런스 플랜 — 현금 → 밴드 초과 과소 카테고리 배분 (매도 0건) */
  rebalancePlan: (strategy: string, cash: number, profile?: string) =>
    apiClient.get<RebalancePlanVO>(`/v1/optimizer/rebalance-plan`, { strategy, cash, profile: profile || undefined }),

  coach: (strategy: string, profile?: string, forceRefresh = false) =>
    apiClient.get<{ report: string; generated_at: string; from_cache: boolean }>(`/v1/insights/portfolio-coach`, { strategy, profile: profile || undefined, force_refresh: forceRefresh || undefined }, { timeout: 300_000 }),

  backtestCompare: (profile = "neutral", optWindow = 500) =>
    apiClient.get<BacktestCompareVO>(`/v1/optimizer/backtest-compare`, { profile, opt_window: optWindow }, { timeout: 300_000 }),

  // ── 배분 목표 전략 적용 (스냅샷 방식, 2026-08-22) ──
  applyStrategy: (strategy: string, profileKey?: string) =>
    apiClient.put<{ ok: boolean; strategy: string; label: string; profile_key: string; snapshot: boolean }>(
      `/v1/optimizer/strategy`,
      {},
      { params: { strategy, profile_key: profileKey || undefined }, timeout: 120_000 },
    ),

  getStrategy: () =>
    apiClient.get<{
      strategy_choice: string | null;
      profile_key: string | null;
      updated_at: string | null;
      active_config: {
        strategy_name: string;
        config: {
          strategy_name: string;
          description: string;
          categories: { name: string; target_pct: number; tickers: string[]; stock_names: string[] }[];
          band_threshold_pct: number;
        };
      } | null;
    }>(`/v1/optimizer/strategy`),

  // ── 리밸런싱 제외 계좌 (2026-09-22) ──
  getExcludedAccounts: () =>
    apiClient.get<{ accounts: string[]; available_accounts: string[] }>(`/v1/optimizer/excluded-accounts`),

  saveExcludedAccounts: (accounts: string[]) =>
    apiClient.put<{ ok: boolean; accounts: string[] }>(`/v1/optimizer/excluded-accounts`, { accounts }),
};
